import { Tooltip } from "@carbon6/c6-frontend-design";
import type { ReactNode } from "react";
import type { RegistryDatasetRef } from "@/api/training";
import { CopyButton } from "@/components/buttons";
import { RegistryDatasetRefLink } from "./trainingRefs";
import styles from "./EvalRunDetailView.module.css";

export function EvalRunDetailSection({
  title,
  actions,
  children,
}: {
  title: string;
  actions?: ReactNode;
  children: ReactNode;
}): ReactNode {
  return (
    <section className={styles.section}>
      {actions ? (
        <div className={styles.sectionHeader}>
          <h3 className={styles.sectionTitle}>{title}</h3>
          {actions}
        </div>
      ) : (
        <h3 className={styles.sectionTitle}>{title}</h3>
      )}
      {children}
    </section>
  );
}

export function EvalRunDetailInfoList({ children }: { children: ReactNode }): ReactNode {
  return <dl className={styles.infoList}>{children}</dl>;
}

export function EvalRunDetailInfo({
  label,
  value,
  copyText,
}: {
  label: string;
  value: ReactNode;
  /** 값 오른쪽의 복사 버튼이 복사할 원본 문자열이다. */
  copyText?: string | null;
}): ReactNode {
  return (
    <div className={styles.infoRow}>
      <dt>{label}</dt>
      <dd>
        {value}
        {copyText ? (
          <span className={styles.copyAffordance}>
            <CopyButton mode="icon" text={copyText} ariaLabel={`${label} 복사`} />
          </span>
        ) : null}
      </dd>
    </div>
  );
}

export function EvalRunDetailDatasetValue({
  upstreamLabel,
  registryRef,
}: {
  upstreamLabel: string | null;
  registryRef: RegistryDatasetRef | null | undefined;
}): ReactNode {
  // 입력 자체가 없는 행과 "이름을 모르는 입력" 은 다르다. 입력이 없는 행만 대시로 표시한다.
  if (!upstreamLabel && !registryRef) return "—";
  return <RegistryDatasetRefLink registryRef={registryRef} upstreamLabel={upstreamLabel} />;
}

export function EvalRunDetailChip({
  label,
  value,
  description,
}: {
  label: string;
  value: ReactNode;
  description?: string | null;
}): ReactNode {
  const content = (
    <>
      <span className={styles.muted}>{label}</span>
      <span>{value}</span>
    </>
  );
  return description?.trim() ? (
    <Tooltip content={description} className={styles.chipTooltip}>
      <button type="button" className={styles.chip}>
        {content}
      </button>
    </Tooltip>
  ) : (
    <span className={styles.chip}>{content}</span>
  );
}

export function EvalRunDetailSummaryStat({
  label,
  value,
}: {
  label: string;
  value: string;
}): ReactNode {
  return (
    <div className={styles.stat}>
      <span className={styles.muted}>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

export function EvalRunDetailNotice({
  tone,
  children,
}: {
  tone: "error" | "warning" | "neutral";
  children: ReactNode;
}): ReactNode {
  return <div className={`${styles.notice} ${styles[tone]}`}>{children}</div>;
}
